import { useEffect } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';

import { TableOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { DealClassifierFiltersDto, FindDealClassifiersRes } from '@dealstack/shared/lib/generated';
import { RecordAny } from '@dealstack/shared/lib/common/common';
import { getDealClassifiers } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/hooks/requests';

export type GetDealClassifierFilters = {
	pagination?: TableOperations<RecordAny>['pagination'];
	filter: DealClassifierFiltersDto;
	sortBy?: {
		key: string;
		desc: boolean;
	};
};

interface Props {
	onSuccess?: (results: ApiV1.Response<FindDealClassifiersRes>) => void;
	filters?: GetDealClassifierFilters;
	dealId?: string;
}

export const useGetDealClassifiers = ({ onSuccess, filters, dealId }: Props) => {
	const q = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.DEAL_DEALID_CLASSIFIERS_GET, {
				dealId: dealId ?? '',
			}),
			{
				filters: {
					filter: {
						classifierName: filters?.filter?.classifierName ?? undefined,
						getAll: filters?.filter?.getAll ?? undefined,
					},
					pagination: filters?.pagination ?? undefined,
					sortBy: filters?.sortBy,
				},
			},
		],
		queryFn: ({ signal }) =>
			getDealClassifiers(
				formatUnicorn(API_PATHS.DEAL_DEALID_CLASSIFIERS_GET, {
					dealId: dealId ?? '',
				}),
				{
					filters: {
						filter: {
							classifierName: filters?.filter?.classifierName ?? undefined,
							getAll: filters?.filter?.getAll ?? undefined,
						},
						pagination: filters?.pagination ?? undefined,
						sortBy: filters?.sortBy,
					},
				},
				signal
			),
		placeholderData: keepPreviousData,
	});

	useEffect(() => {
		if (q.isSuccess) {
			onSuccess?.(q.data);
		}
	}, [q.isSuccess, q.data]);

	return {
		classifiers: q.data,
		isClassifiersLoading: q.isLoading,
		classifierError: q.error,
	};
};
